import { useEffect, useState, useCallback } from 'react';

interface SectionInfo {
  id: string;
  element: HTMLElement | null;
}

/**
 * Hook that tracks which section is currently in view.
 * Uses IntersectionObserver for efficient scroll tracking.
 */
export function useActiveSection(sectionIds: string[], threshold = 0.35) {
  const [activeSection, setActiveSection] = useState<string>(sectionIds[0] || '');

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      }
    },
    [],
  );

  useEffect(() => {
    const sections: SectionInfo[] = sectionIds.map((id) => ({
      id,
      element: document.getElementById(id),
    }));

    const observer = new IntersectionObserver(handleIntersect, {
      root: null,
      rootMargin: '-80px 0px -35% 0px',
      threshold,
    });

    for (const section of sections) {
      if (section.element) {
        observer.observe(section.element);
      }
    }

    return () => observer.disconnect();
  }, [sectionIds, threshold, handleIntersect]);

  return activeSection;
}
